import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { ExpandMore, InsertDriveFile } from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useReactToPrint } from "react-to-print";
import { useAuth } from "oidc-react";
import { getUserRole, isJsonParse } from "../../../../../_helper/helper";
import {
  getApplicationByApplicationIdApi,
  getCitizenshipApi,
  getAgeRangesApi,
  getEthnicitiesApi,
  getPersonRacesApi,
  getJobApplicationLeadsApi,
  getPositionTypesApi,
} from "../../../../../_helper/services/onlineJobApplication";

// ---------------------------------------------------------------------------
// Read-only replica of the applicant's generated "Application PDF" (General
// Instructions + Form A1/A2/A3 + Application Information + Document
// Attachment), rendered as an HTML page instead of an opaque PDF file so it
// can be viewed inline and downloaded from here directly.
//
// MOCK_APPLICATION_DATA below is only a design-time fallback, used when the
// page is rendered without live `applicationId`/`announcementId` ids and
// without an `applicationData` override. See mapApplicationResponse() for
// the real GetApplicationV1 -> view model mapping.
// ---------------------------------------------------------------------------

const MOCK_APPLICATION_DATA = {
  applicantName: "Test Test",
  positionTitle: "LIBRARY TECHNICIAN I",
  jobPositionNo: "0350X03530",
  announcementDate: "08/31/2026",
  lowestSalaryAcceptable: "20000",

  eeo: {
    citizenship: "UNITED STATES",
    howLearned: "Job Announcement.",
    howLearnedSpecify: "",
    gender: "",
    maritalStatus: "",
    ageGroup: "",
    ethnicity: "",
    races: ["WHITE"],
  },

  personal: {
    lastName: "Test",
    firstName: "Test",
    middleName: "",
    mailing: { line: "test", city: "test", state: "te", zip: "123" },
    physical: { line: "test", city: "test", state: "TS", zip: "123" },
    phone: { home: "54564658", email: "vermaanurag222@gmail.com", cell: "45646" },
  },

  education: {
    highSchool: { school: "", location: "", yearGraduated: "" },
    ged: { school: "", location: "", certificateNo: "", yearGraduated: "" },
    lastGradeCompleted: "",
    higherEducation: {
      school: "",
      location: "",
      semester: "",
      quarter: "",
      fromDate: "",
      endDate: "",
      courseOfStudy: "",
      degreeType: "",
      yearEarned: "2026",
    },
    undergraduateCourses: { major: "", semesterHours: "", quarterHours: "" },
    graduateCourses: { major: "", semesterHours: "", quarterHours: "" },
    manualsEquipmentLicenses: "",
  },

  workExperiences: [
    {
      employer: "test",
      isPresentEmployer: false,
      isLastEmployer: true,
      startDate: "09/01/2025",
      endDate: "06/01/2026",
      hoursPerWeek: "20-35",
      positionTitle: "test",
      salary: "25000",
      reasonForLeaving: "",
      businessType: "",
      supervisory: false,
      nonSupervisory: false,
      permanent: true,
      temporary: false,
      employerAddress: { line: "", city: "", state: "", zip: "" },
      employerPhone: "",
      immediateSupervisor: "",
      specificDuties: ["", "", "", "", ""],
    },
    {
      employer: "test 2",
      isPresentEmployer: false,
      isLastEmployer: true,
      startDate: "07/01/2026",
      endDate: "08/07/2026",
      hoursPerWeek: "36 or More",
      positionTitle: "tester",
      salary: "30000",
      reasonForLeaving: "",
      businessType: "",
      supervisory: false,
      nonSupervisory: false,
      permanent: true,
      temporary: false,
      employerAddress: { line: "", city: "", state: "", zip: "" },
      employerPhone: "",
      immediateSupervisor: "",
      specificDuties: ["", "", "", "", ""],
    },
  ],

  isGovGuamAutonomousEmployee: false,
  preferentialHireStatus: "N/A",
  personalContacts: [
    { name: "", address: "", phone: "" },
    { name: "", address: "", phone: "" },
    { name: "", address: "", phone: "" },
  ],
  applicantSignatureA2: null,

  preferencePoints: {
    veteranOption: "NA",
    branch: "",
    dischargeType: "",
    startDate: "",
    endDate: "",
    disabilityOption: "NA",
    dateOfCertification: "",
  },
  applicantSignatureA3: null,

  applicationInfo: {
    experience: "N/A",
    requiredSkills: "N/A",
    additionalSkills: "N/A",
    certifications: "N/A",
    employment: "N/A",
    description: "N/A",
  },

  documentAttachments: [
    {
      label: "HS Diploma/GED (College Transcript may be used)",
      mandatory: true,
      description: "Please submit HS Diploma/GED (College Transcript may be used).",
      fileName:
        "OLA-00000000DOA-26-174-HS Diploma/GED (College Transcript may be used)-01/09/2026-21:56:34",
    },
  ],
};

const parseArray = (value) => (Array.isArray(value) ? value : isJsonParse(value) || []);

// Work experience dates come back as ISO datetime strings (e.g.
// "2020-03-01T00:00:00") -- reformat to mm-dd-yyyy for display. Leave
// anything already in mm-dd-yyyy (or an unrecognized format) untouched.
const formatDate = (value) => {
  if (!value) return "";
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!isoMatch) return value;
  const [, year, month, day] = isoMatch;
  return `${month}-${day}-${year}`;
};

const findLookupLabel = (list, id) => {
  if (id === null || id === undefined || id === "") return "";
  const match = (Array.isArray(list) ? list : []).find((item) => String(item?.id) === String(id));
  return match?.description || match?.name || "";
};

// Best-effort mapping from the raw GetApplicationV1 record into the flat view
// model this page renders. Field names follow the applicant-facing OJA forms
// (jobEmployeeApplicationForm.js / jobOpportunityDataForm.js /
// jobPreferencePointRequests.js), since GetApplicationV1 reads from the same
// application entity as those forms' GetApplicationV2.
const mapApplicationResponse = (raw, lookups = {}) => {
  if (!raw) return null;
  const { positionTypes = [], citizenship = [], ageRanges = [], ethnicities = [], personRaces = [], leads = [] } = lookups;

  const higherEducations = parseArray(raw.higherEducation);
  const firstHigherEd = higherEducations[0] || {};
  const undergradCourse = (firstHigherEd.majorUnderGraduateCourses || [])[0] || {};
  const gradCourse = (firstHigherEd.majorGraduateCourses || [])[0] || {};
  const personalContacts = parseArray(raw.personalContact);
  const mapDocument = (doc) => ({
    label: doc.contentType || "",
    fileName: doc.fileName || "",
    documentUrl: doc.documentFile || "",
  });
  const isAppealDocument = (doc) => (doc.fileName || "").includes("Appeal-Document");
  const allDocuments = raw.documents || [];
  const policeCombat = parseArray(raw.policeCombatPreference)[0] || {};
  const races = parseArray(raw.personsRace)
    .map((r) => findLookupLabel(personRaces, r?.raceId))
    .filter(Boolean);

  const positionTypeLabel = (id) => findLookupLabel(positionTypes, id).toLowerCase();
  const workExperiences = (raw.workExperiences || []).map((exp) => {
    const duties = parseArray(exp.jobPositionQa);
    const type1 = positionTypeLabel(exp.currentPosition1Id);
    const type2 = positionTypeLabel(exp.currentPosition2Id);
    return {
      employer: exp.nameOfEmployer || "",
      isPresentEmployer: exp.employerType === "presentEmployer",
      isLastEmployer: exp.employerType === "lastEmployer",
      startDate: formatDate(exp.startDate),
      endDate: formatDate(exp.endDate),
      hoursPerWeek: exp.hoursWorkedPerWeek || "",
      positionTitle: exp.positionDesignation || "",
      salary: exp.salary || "",
      reasonForLeaving: exp.reasonForLeaving || "",
      businessType: exp.businessType || "",
      supervisory: type1 === "supervisory",
      nonSupervisory: type1.includes("non-supervisory") || type1.includes("non supervisory"),
      permanent: type2 === "permanent",
      temporary: type2 === "temporary",
      employerAddress: {
        line: exp.maStreetNo || "",
        city: exp.maCity || "",
        state: exp.maState || "",
        zip: exp.maZipcode || "",
      },
      employerPhone: exp.employerTelNo || "",
      immediateSupervisor: exp.employerImmediateSupervisor || "",
      specificDuties: duties.length ? duties.map((d) => d?.questionText || "") : [""],
    };
  });

  const preferentialHireMap = { 0: "Yes", 1: "No", 2: "N/A" };
  const veteranTypeLabel = String(raw.policeCombatPreferenceType || "").toLowerCase();
  const veteranOption = veteranTypeLabel.includes("10")
    ? "10"
    : veteranTypeLabel.includes("5") || veteranTypeLabel.includes("veteran") || veteranTypeLabel.includes("police")
      ? "5"
      : "NA";

  return {
    applicantName: `${raw.firstName || ""} ${raw.lastName || ""}`.trim(),
    positionTitle: raw.positionTitleAppliedFor || raw.positionTitle || raw.PositionTitle || "",
    jobPositionNo: raw.positionNo || raw.POSITION_NO || raw.jobPositionNo || "",
    announcementDate: formatDate(raw.announcementDate || raw.dateClosed),
    lowestSalaryAcceptable: raw.lowestSalaryAcceptable || "",

    eeo: {
      citizenship: findLookupLabel(citizenship, raw.citizenshipId),
      howLearned: findLookupLabel(leads, raw.jobApplicationLeadId),
      howLearnedSpecify: raw[`leadSpecification${raw.jobApplicationLeadId}`] || raw.leadSpecification || "",
      gender: raw.sex === "M" ? "Male" : raw.sex === "F" ? "Female" : raw.sex || "",
      maritalStatus: raw.maritalStatus || "",
      ageGroup: findLookupLabel(ageRanges, raw.ageId),
      ethnicity: findLookupLabel(ethnicities, raw.ethinicityId),
      races,
    },

    personal: {
      lastName: raw.lastName || "",
      firstName: raw.firstName || "",
      middleName: raw.middleName || "",
      mailing: { line: raw.mlAddLn1 || "", city: raw.mlAddCity || "", state: raw.mlAddState || "", zip: raw.mlAddZip || "" },
      physical: { line: raw.hmAddLn1 || "", city: raw.hmAddCity || "", state: raw.hmAddState || "", zip: raw.hmAddZip || "" },
      phone: { home: raw.hmPhone || "", email: raw.email || "", cell: raw.cellPhone || "" },
    },

    education: {
      highSchool: { school: raw.hsgschoolName || "", location: raw.hsglocation || "", yearGraduated: raw.hsgyearGraduate || "" },
      ged: { school: raw.gedschoolName || "", location: raw.gedlocation || "", certificateNo: raw.gedcertificationNo || "", yearGraduated: raw.gedyearGraduate || "" },
      lastGradeCompleted: raw.lastGradeCompletedIn || "",
      lastGradeSchool: raw.lastGradeSchool || "",
      higherEducation: {
        school: firstHigherEd.collegeName || "",
        location: firstHigherEd.location || "",
        semester: firstHigherEd.semester || "",
        quarter: firstHigherEd.quarter || "",
        fromDate: formatDate(firstHigherEd.startDate),
        endDate: formatDate(firstHigherEd.endDate),
        courseOfStudy: firstHigherEd.course || "",
        degreeType: firstHigherEd.degreeType || "",
        yearEarned: firstHigherEd.degreeYearEarned || "",
      },
      undergraduateCourses: { major: undergradCourse.courseName || "", semesterHours: undergradCourse.semesterHours || "", quarterHours: undergradCourse.quarterHours || "" },
      graduateCourses: { major: gradCourse.courseName || "", semesterHours: gradCourse.semesterHours || "", quarterHours: gradCourse.quarterHours || "" },
      manualsEquipmentLicenses: raw.appliedPositionQualifications || "",
    },

    workExperiences,

    isGovGuamAutonomousEmployee: raw.atonomousAgnecy === true,
    preferentialHireStatus: preferentialHireMap[raw.preferentialHire] || "N/A",
    personalContacts: [0, 1, 2].map((i) => ({
      name: personalContacts[i]?.refName || "",
      address: personalContacts[i]?.refAddress || "",
      phone: personalContacts[i]?.refTelephoneNo || "",
    })),
    applicantSignatureA2: raw.employeeApplicationSignature || null,

    preferencePoints: {
      veteranOption,
      branch: policeCombat.branch || "",
      dischargeType: policeCombat.dischargeType || "",
      startDate: formatDate(policeCombat.startDate),
      endDate: formatDate(policeCombat.endDate),
      disabilityOption: raw.disabilityPreferencePoints ? "5" : "NA",
      dateOfCertification: formatDate(raw.dateOfCertification),
    },
    applicantSignatureA3: raw.preferencePointsSignature || null,

    applicationInfo: applicationInfo(raw),
    documentAttachments: allDocuments.filter((doc) => !isAppealDocument(doc)).map(mapDocument),

    // A separate appeal filed against this application -- only shown when
    // the applicant actually has one on file.
    appeal: raw.appeal_Remarks || raw.appeal_Date
      ? {
          remarks: raw.appeal_Remarks || "",
          date: formatDate(raw.appeal_Date),
          documents: allDocuments.filter(isAppealDocument).map(mapDocument),
        }
      : null,
  };
};

// "Application Information" is auto-derived server-side from the applicant's
// profile/resume (experience, skills, certifications, etc.) rather than
// being a form field itself, so all six pieces come from their own raw
// fields instead of a nested object. Leave the section out entirely (return
// null) when none of them have anything to show, rather than rendering six
// blank boxes.
const applicationInfo = (raw) => {
  const info = {
    experience: raw.experienceLastFiveYears || "",
    requiredSkills: raw.requiredSkillsLastFiveYears || "",
    additionalSkills: raw.additionalSkills || "",
    certifications: raw.certificationsAndLicenses || "",
    employment: raw.govtEmployment || "",
    description: raw.anyDescription || "",
  };
  return Object.values(info).some(Boolean) ? info : null;
};

const SECTION_HEADER_SX = {
  backgroundColor: "#e8eef2",
  minHeight: 48,
  "&.Mui-expanded": { minHeight: 48 },
};

const Section = ({ title, children, defaultExpanded = true, action = null }) => (
  <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 2 }} disableGutters>
    <AccordionSummary expandIcon={<ExpandMore />} sx={SECTION_HEADER_SX}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        {action}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 2.5 }}>{children}</AccordionDetails>
  </Accordion>
);

const Field = ({ label, value, sx, required, bulletList }) => {
  // Application Information's fields are newline-separated lists (one line
  // per skill/experience/certification) -- render those as bullets instead
  // of one run-on line.
  const lines = bulletList && value ? String(value).split("\n").map((line) => line.trim()).filter(Boolean) : null;
  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
        {label} {required && <Box component="span" sx={{ color: "error.main" }}>*</Box>}
      </Typography>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1.5,
          py: 1,
          minHeight: 22,
          backgroundColor: value ? "#eef4fb" : "#fff",
          fontSize: 14,
          wordBreak: "break-word",
        }}
      >
        {lines ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {lines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </Box>
        ) : (
          value || "N/A"
        )}
      </Box>
    </Box>
  );
};

const FieldRow = ({ children, columns = "repeat(auto-fit, minmax(200px, 1fr))" }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: columns, gap: 2 }}>{children}</Box>
);

const CheckItem = ({ label, checked }) => (
  <FormControlLabel
    sx={{ mr: 3 }}
    control={<Checkbox checked={!!checked} size="small" disabled />}
    label={<Typography variant="body2">{label}</Typography>}
  />
);

const TextBlock = ({ children }) => (
  <Typography variant="body2" sx={{ textAlign: "justify", mb: 1.5, lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const SignatureBox = ({ label, signatureUrl }) => (
  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
    <Box sx={{ textAlign: "center" }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label} <Box component="span" sx={{ color: "error.main" }}>*</Box>
      </Typography>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          width: 220,
          height: 90,
          mt: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {signatureUrl ? (
          <img src={signatureUrl} alt={label} style={{ maxWidth: "100%", maxHeight: "100%" }} />
        ) : (
          <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
            No signature on file
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

const DocumentCard = ({ label, fileName, documentUrl }) => (
  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </Box>
    <Box
      component={documentUrl ? "a" : "div"}
      href={documentUrl || undefined}
      target={documentUrl ? "_blank" : undefined}
      rel={documentUrl ? "noopener noreferrer" : undefined}
      sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#f5f7fa", borderRadius: 1, px: 1.5, py: 1, maxWidth: 320, textDecoration: "none", color: "inherit", cursor: documentUrl ? "pointer" : "default" }}
    >
      <InsertDriveFile fontSize="small" color="error" />
      <Typography variant="caption" sx={{ wordBreak: "break-word" }}>{fileName}</Typography>
    </Box>
  </Box>
);

// Downloading is triggered from the parent Modal's header (see
// CertificationListTwo.jsx / LookupMaster/JobApplications), so the download
// action and its in-progress state are exposed via ref/callback instead of
// rendering a button inside this page.
const ApplicationPdfPage = forwardRef(({ applicationData, applicationId, announcementId, onClose, onDownloadingChange, onReady }, ref) => {
  const contentRef = useRef(null);
  // "Appeal Information" (when present) always starts on its own page.
  // triggerPrint's real browser print honors the CSS break below directly;
  // buildPdf's canvas-slice approach has no such concept, so it pads a
  // spacer above this marker (see buildPdf) to land the section exactly at
  // the top of the next sliced page instead.
  const appealSectionRef = useRef(null);
  const appealSpacerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const { userData } = useAuth();
  // A caller can render this page off-screen purely to trigger a download
  // (see CertificatePrepare.jsx) and unmount it as soon as download() resolves
  // -- guards the post-await setDownloading(false) below from firing after
  // that unmount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Form A1 carries the applicant's EEO self-identification data, so only the
  // EEO role can see it -- everyone else gets the rest of the application.
  const isEeoRole = getUserRole(userData) === "EEO";

  // Live data path: fetch GetApplicationV1 (by APPLICATION_ID + ANNOUNCEMENT_ID)
  // whenever both ids are supplied, instead of relying on the mock. The EEO
  // lookup lists (citizenship/age/ethnicity/race/leads) are only needed to
  // resolve Form A1's ids to labels, so they're skipped for non-EEO viewers.
  const usingLiveData = Boolean(applicationId && announcementId);
  const [fetchedData, setFetchedData] = useState(null);
  const [loadingApplication, setLoadingApplication] = useState(usingLiveData);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!usingLiveData) return undefined;
    let cancelled = false;
    setLoadingApplication(true);
    setLoadError(false);

    const lookups = isEeoRole
      ? Promise.all([
        getCitizenshipApi().catch(() => []),
        getAgeRangesApi().catch(() => []),
        getEthnicitiesApi().catch(() => []),
        getPersonRacesApi().catch(() => []),
        getJobApplicationLeadsApi().catch(() => []),
      ])
      : Promise.resolve([[], [], [], [], []]);

    Promise.all([
      getApplicationByApplicationIdApi({ applicationId, announcementId }),
      getPositionTypesApi().catch(() => []),
      lookups,
    ])
      .then(([raw, positionTypes, [citizenship, ageRanges, ethnicities, personRaces, leads]]) => {
        if (cancelled) return;
        setFetchedData(
          mapApplicationResponse(raw, { positionTypes, citizenship, ageRanges, ethnicities, personRaces, leads })
        );
      })
      .catch((error) => {
        console.error("Error fetching application (GetApplicationV1):", error);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingApplication(false);
      });

    return () => {
      cancelled = true;
    };
  }, [usingLiveData, applicationId, announcementId, isEeoRole]);

  const data = usingLiveData ? fetchedData : applicationData || MOCK_APPLICATION_DATA;

  // Used only by getPdfBase64 now -- the user-facing "Download" button below
  // went through two rounds of html2canvas/jsPDF width fixes and still
  // composited a gray band (the modal backdrop / off-screen clone bleeding
  // through) onto every page, so handleDownload was switched to
  // react-to-print (the browser's own print pipeline, already used
  // elsewhere in this codebase for Form7/Form8/the Certification Packet)
  // instead. That approach has no canvas/backdrop compositing to get wrong.
  // This screenshot-based path stays only for a base64 PDF without a visible
  // print dialog, since react-to-print can't produce one programmatically.
  const buildPdf = async () => {
    if (!contentRef.current) return null;
    // scale: 2 + PNG took a long time on a page this long -- scale: 1 +
    // JPEG cuts both the pixel count (4x fewer than scale 2) and the
    // encode time, while staying sharp enough to read.
    // windowWidth must match contentRef's actual rendered width, not a fixed
    // guess -- this page is captured from two very different contexts (an
    // 816px-wide off-screen container for row-level downloads, vs. the much
    // wider size="xl" Modal for the View Application download button). A
    // mismatched windowWidth makes html2canvas lay the clone out narrower
    // than the element really is, and the leftover capture area on the right
    // then bleeds through to the modal backdrop behind it instead of staying
    // blank -- showing up as a gray band down every page.
    const captureWidth = contentRef.current.scrollWidth || contentRef.current.offsetWidth || 816;

    const pdf = new jsPDF("p", "pt", "legal");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Pad a spacer above "Appeal Information" (if present) tall enough that
    // the section lands exactly at the top of the next pageHeight-tall
    // slice below, instead of wherever it naturally falls mid-page.
    if (appealSectionRef.current && appealSpacerRef.current) {
      appealSpacerRef.current.style.height = "0px";
      const pageHeightPx = (pageHeight / pageWidth) * captureWidth;
      const offsetPx =
        appealSectionRef.current.getBoundingClientRect().top - contentRef.current.getBoundingClientRect().top;
      const remainder = offsetPx % pageHeightPx;
      appealSpacerRef.current.style.height = remainder === 0 ? "0px" : `${Math.ceil(pageHeightPx - remainder)}px`;
      // Let the spacer's new height reflow before capturing.
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    let canvas;
    try {
      canvas = await html2canvas(contentRef.current, {
        useCORS: true,
        scale: 1,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: captureWidth,
        ignoreElements: (el) => {
          if (contentRef.current?.contains(el)) return false;
          if (el.contains?.(contentRef.current)) return false;
          if (el.classList?.contains("modal-backdrop")) return true;
          if (el.classList?.contains("modal") || el.classList?.contains("modal-open")) return true;
          return window.getComputedStyle(el).position === "fixed";
        },
      });
    } finally {
      if (appealSpacerRef.current) appealSpacerRef.current.style.height = "0px";
    }

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL("image/jpeg", 0.92);

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
      heightLeft -= pageHeight;
    }

    return pdf;
  };

  // Resolved from onAfterPrint below, so handleDownload can stay awaitable
  // for callers that unmount this page as soon as it resolves (see
  // CertificatePrepare.jsx's off-screen applicationDownloadTarget flow).
  const printResolveRef = useRef(null);

  const triggerPrint = useReactToPrint({
    content: () => contentRef.current,
    documentTitle: `Application-${data?.applicantName || "Applicant"}`,
    onBeforeGetContent: () =>
      new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))),
    // Size left unset (auto) so whatever paper option the user picks in the
    // browser's own print/save-as-PDF dialog is used, same convention
    // CertificatePrepare.jsx's Certification Packet print uses.
    pageStyle: `
      @page { size: auto; }
      body { margin: 0; }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    `,
    onBeforePrint: () => setDownloading(true),
    onAfterPrint: () => {
      if (isMountedRef.current) setDownloading(false);
      printResolveRef.current?.();
      printResolveRef.current = null;
    },
  });

  const handleDownload = () => {
    if (!contentRef.current) return Promise.resolve();
    return new Promise((resolve) => {
      printResolveRef.current = resolve;
      triggerPrint();
    });
  };

  // Returns the same generated PDF as a base64 data URI instead of triggering
  // a browser download -- used when this page is rendered off-screen to
  // auto-attach the Application PDF during GG1 submission (see
  // CertificationListTwo.jsx's captureApplicationPdfAsBase64).
  const getPdfBase64 = async () => {
    const pdf = await buildPdf();
    return pdf ? pdf.output("datauristring") : null;
  };

  useImperativeHandle(ref, () => ({ download: handleDownload, getPdfBase64 }));

  useEffect(() => {
    onDownloadingChange?.(downloading);
  }, [downloading, onDownloadingChange]);

  // Fires once real content (live-fetched or mock/passed-in) has painted into
  // contentRef, so a caller rendering this page off-screen knows it's safe to
  // call download()/getPdfBase64() -- there's nothing to capture while the
  // loading spinner or the error message is showing instead.
  // Deferred a tick (setTimeout 0, cleared on cleanup) rather than called
  // synchronously: React 18 StrictMode dev-only mounts this effect, tears it
  // down, then mounts it again for the same commit, and a caller whose
  // onReady triggers download() (react-to-print, itself effect/ref driven)
  // straight from that synchronous first pass can lose the race and log
  // "nothing to print". The cleanup cancels the phantom pass's timer, so
  // onReady only actually fires once, after that dance has settled.
  useEffect(() => {
    if (usingLiveData && (loadingApplication || loadError || !data)) return;
    const timer = setTimeout(() => onReady?.(), 0);
    return () => clearTimeout(timer);
  }, [usingLiveData, loadingApplication, loadError, data, onReady]);

  if (usingLiveData && loadingApplication) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 3 }}>
        <CircularProgress size={18} /> Loading application...
      </Box>
    );
  }

  if (usingLiveData && (loadError || !data)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          We are unable to load this application at this time. Please try again shortly.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box ref={contentRef} sx={{ backgroundColor: "#fff", p: 1 }}>
        <Section title="General Instructions & Information">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>SUBMITTING YOUR APPLICATION</Typography>
          <TextBlock>
            Complete this application by providing requested information in each field. If you wish to submit a RESUME,
            your resume must contain all of the required information under Work Experience Section, for each work
            described. <b>You must submit an application for each currently announced position you are applying.</b> All
            applications being submitted must comply with the deadline stated on the JOB ANNOUNCEMENT.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>RATING PROCESS</Typography>
          <TextBlock>
            The contents of the employment application and other substantiating documents will be reviewed to determine
            if you meet the minimum qualification requirements of the position. Volunteer work and employment in the
            military service on a part time basis as well as work experience in a detailed capacity will be credited
            based on their own merits. You may be rated ineligible if you do not provide sufficient information and/or
            supporting documents. <b>Submission of new information on education and/or work experience after an
              eligibility list is established is prohibited.</b> If certified for employment consideration, you will be
            required to fill out a "Suitability Determination" form.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>NOTIFICATION OF RESULTS</Typography>
          <TextBlock>
            Your employment application is part of an examination process. You may be scheduled for additional
            examinations depending on the position requirements. The results will be emailed to you. <b>IT IS YOUR
              RESPONSIBILITY TO INFORM THIS OFFICE OF ANY CHANGES TO YOUR ADDRESS OR TELEPHONE NUMBER.</b>
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>EVALUATION METHODS</Typography>
          <TextBlock>
            To determine your qualifications for the position which you are applying, job related tests designed to
            reveal your capacity to successfully perform the duties of the position are utilized. Most positions
            require an evaluation of your application to determine your qualification based on a rating of your
            education and experience. Additional examinations such as a written and a performance test may be required
            depending on the particular job requirements of the position. The top eligibles will be referred for
            employment consideration for each vacancy subject to the Personnel Rules and Regulations of the respective
            department or agency. If a selection interview is required, you will be notified. Failure to submit to
            employment examination requirements will result in an ineligible rating.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>DRUG SCREENING</Typography>
          <TextBlock>
            Upon selection for employment into the Government of Guam, you must take and pass urinalysis testing for
            illegal use of drugs. In addition, government employees are subject to their respective Drug-Free Work
            Place Program requirements. Failure to submit to drug testing will result in immediate disqualification or
            disciplinary action.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>PRE-EMPLOYMENT MEDICAL EXAMINATION</Typography>
          <TextBlock>
            All applicants accepting employment with the government must take and pass a pre-entry physical
            examination as a condition of employment or continued employment. Applicants accepting employment with
            educational institutions and/or agencies requiring health clearance must take and pass a pre-entry and
            annual Tuberculosis Test as a condition of employment. All applicants/employees are responsible for all
            expenses incurred for this examination. Failure to satisfactorily meet or complete the specific
            requirements of the examination may result in your disqualification for or termination from employment.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>BACKGROUND INVESTIGATION</Typography>
          <TextBlock>
            When you sign this job application, you authorize the government to seek and obtain information regarding
            your suitability for employment. All factors which are job related may be investigated (e.g., previous
            employment, educational credentials, and criminal record). All information obtained may be used to
            determine your eligibility for employment in accordance with equal employment opportunity guidelines. In
            addition, when you sign this application, you release previous employers and job related sources from
            legal liability for the information they provide.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>PROBATIONARY PERIOD</Typography>
          <TextBlock>
            If you are selected for permanent appointment to a classified position, you must initially undergo a
            probationary period subject to the Personnel Rules and Regulations of your respective department or
            agency. All temporary or Limited Term employees do not serve a probationary period and are subject to
            termination at will.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>REQUIRED DOCUMENTS</Typography>
          <TextBlock>
            To validate credentials you may claim, (e.g. High School Diploma, College Transcript, DD-214), <b>an
              original or certified copy of the document(s) must accompany the application.</b> Failure to provide proof
            may result in your disqualification. Additionally, please refer to the specific job announcement for all
            other required documents needed.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>HANDBOOKS AND STUDY GUIDES</Typography>
          <TextBlock>
            An Applicant Handbook describing the application process and Study Guides for written examinations are
            available upon request at the Department of Administration or the respective department or agency.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>U.S. MILITARY PREFERENCE POINTS</Typography>
          <TextBlock>
            As a member of the Armed Forces of the United States or the Guam Police Combat Patrol, you are entitled to
            claim five preference points, if you have completed at least 180 cumulative days of active duty and
            received an honorable discharge. <b>To claim the points, you must fill out a "Preference Points" request
              form</b> and provide your DD-214, which indicates your service dates and character of service. To claim an
            additional five (5) points for disability, you must provide a letter from the U.S. Veteran's
            Administration or the Department of Veteran's Affairs, which specifically states that you are entitled to
            Civil Service Preference for a service connected disability. If eligible for any of the preference points,
            the points will be added to your passing final earned rating. Preference points are only awarded for
            initial employment.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>PREFERENCE POINTS FOR PERSONS WITH DISABILITIES</Typography>
          <TextBlock>
            As a person with a disability, you are entitled to claim five preference points, if you are certified with
            a disability. <b>To claim the points, you must fill out a "Preference Points" request form</b> and provide a
            certification letter from the Department of Public Health and Social Services. Preference points are only
            awarded for initial employment.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>PREFERENTIAL HIRE STATUS</Typography>
          <TextBlock>
            As a recipient of an educational loan or merit scholarship, you are entitled to first offer of employment
            in accordance with Public Law 15-127. To claim preferential hire, you must submit your eligibility letter
            from the University of Guam Financial Aid Office, along with your job application. Preference hiring is
            only awarded for initial employment.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>WORK ELIGIBILITY</Typography>
          <TextBlock>
            U.S. citizens may apply for all Government of Guam jobs. Non U.S. citizens, such as U.S. Permanent
            Residents, citizens of the Federated States of Micronesia, the Republic of the Marshall Islands, and the
            Republic of Palau may apply for employment in MOST GovGuam jobs. Please consult the job announcement for
            any specific requirement. Public Law 99-603 (8 USC Section 1324A) requires the Government of Guam to
            verify your identity and work eligibility. When offered a position, you will be required to provide proof
            of identity and eligibility for employment in the United States. For Additional information, please visit
            the U.S Citizenship and Immigration Services website, www.uscis.gov and review the Employment Eligibility
            Verification, Form I-9.
          </TextBlock>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>MINIMUM EDUCATIONAL REQUIREMENTS</Typography>
          <TextBlock>
            All new employment in the service of the government of Guam shall have, as reasonable measure of job
            performance, minimum requirement of a high school diploma or a successful completion of General Education
            Development (GED) test or any equivalent of a general education high school program, apprenticeship
            program or successful completion of a certification program, from a recognized, accredited or certified
            vocational technical institution, in a specialized field required for the job, pursuant to Public Law
            29-113.
          </TextBlock>
        </Section>

        {isEeoRole && (
          <Section title="Form A1 - Voluntary Data Record Survey (Equal Employment Opportunity Data)">
            <TextBlock>
              The purpose of this form is to monitor the Affirmative Action and Equal Employment Opportunity
              representation within our diverse community. We are seeking your assistance to help us in this effort by
              accurately completing this form. <b>Your cooperation is completely voluntary.</b> The information is for
              data purposes only and will be maintained in a confidential file within the Equal Employment Opportunity
              (EEO) Department, separate from your application.
            </TextBlock>

            <FieldRow>
              <Field label="Position Title Applied For" value={data.positionTitle} />
              <Field label="Job Position No." value={data.jobPositionNo} />
              <Field label="Announcement Date" value={data.announcementDate} />
            </FieldRow>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
              Citizenship <Box component="span" sx={{ color: "error.main" }}>*</Box>
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", mb: 2 }}>
              {["UNITED STATES", "ALIEN AUTHORIZED TO WORK", "PERM RESIDENT ALIEN", "FEDERATED STATES OF MICRONESIA", "MARSHALL ISLANDS", "REPUBLIC OF MARSHALL ISLANDS", "PERMANENT RESIDENT", "REPUBLIC OF PALAU", "OTHERS"].map((opt) => (
                <CheckItem key={opt} label={opt} checked={data.eeo.citizenship === opt} />
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>How did you learn of the job for which you are applying?</Typography>
            <Box sx={{ mb: 2 }}>
              {[
                "Job Information Bulletin Board, Government Agency.",
                "Department of Administration, Human Resources Division Job Information Counter / DOA-HR Website",
                "One Stop Career Center, Department of Labor",
                "Job Announcement.",
                "Newspaper Announcement.",
                "Relative, Friend, or Government Employee",
                "Other",
              ].map((opt) => (
                <Box key={opt} sx={{ display: "flex", alignItems: "center", py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <CheckItem label={opt} checked={data.eeo.howLearned === opt} />
                  {["Job Information Bulletin Board, Government Agency.", "Job Announcement.", "Newspaper Announcement.", "Other"].includes(opt) && (
                    <Box sx={{ ml: "auto", width: 260 }}>
                      <Field label="" value={data.eeo.howLearned === opt ? data.eeo.howLearnedSpecify : ""} sx={{ mb: 0 }} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Gender:</Typography>
            <Box sx={{ mb: 1 }}>
              <CheckItem label="Male" checked={data.eeo.gender === "Male"} />
              <CheckItem label="Female" checked={data.eeo.gender === "Female"} />
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Marital Status:</Typography>
            <Box sx={{ mb: 1 }}>
              {["SINGLE", "MARRIED", "DIVORCED", "LEGALLY SEPARATED", "COMMON-LAW SPOUSE", "WIDOWED", "NOT INDICATED"].map((opt) => (
                <CheckItem key={opt} label={opt} checked={data.eeo.maritalStatus === opt} />
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Age:</Typography>
            <Box sx={{ mb: 1 }}>
              {["17 years and below", "18 years to 39 years", "40 years and above"].map((opt) => (
                <CheckItem key={opt} label={opt} checked={data.eeo.ageGroup === opt} />
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Part 1. Ethnicity: Is the person Hispanic/Latino? (Choose only one)</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", mb: 1 }}>
              {["AFRICAN AMERICAN", "CHINESE", "CHAMORRO", "FILIPINO", "HISPANIC", "JAPANESE", "KOREAN", "MICRONESIA", "MIXED", "NOT IDENTIFIED", "NORTHERN MARIANA", "OTHER", "VIETNAMESE", "CAUCASIAN"].map((opt) => (
                <CheckItem key={opt} label={opt} checked={data.eeo.ethnicity === opt} />
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Part 2. Race: What is the applicant's race (choose one or more)</Typography>
            <Box sx={{ mb: 1 }}>
              {[
                { label: "AMERICAN INDIAN or ALASKA NATIVE", desc: "A person having origins in any of the original peoples of North and South America, including Central America, and who maintain tribal affiliation or community attachment." },
                { label: "ASIAN", desc: "A person having origins in any of the original peoples of the Far East, Southeast Asia, or the Indian Subcontinent, including, for example, Cambodia, China, India, Japan, Korea, Malaysia, Pakistan, the Philippine Islands, Thailand, and Vietnam." },
                { label: "BLACK or AFRICAN AMERICAN", desc: "A person having origins in any of the black racial groups of Africa." },
                { label: "NATIVE HAWAIIAN or OTHER PACIFIC ISLANDER", desc: "A person having origins in any of the peoples of Hawaii, Guam, Samoa, or other Pacific Islands." },
                { label: "WHITE", desc: "A person having origins in any of the original peoples of Europe, the Middle East, or North Africa." },
                { label: "TWO OR MORE RACES", desc: "All persons who identify with more than one of the above five races." },
              ].map((race) => (
                <Box key={race.label} sx={{ display: "flex", alignItems: "flex-start", py: 0.5 }}>
                  <Checkbox checked={data.eeo.races?.includes(race.label)} size="small" disabled sx={{ mt: -0.5 }} />
                  <Typography variant="body2"><b>{race.label}</b> - {race.desc}</Typography>
                </Box>
              ))}
            </Box>

            <TextBlock>
              The government of Guam is an Equal Employment Opportunity Employer. We do not discriminate on the basis of
              race, religion, color, sex (sexual harassment and orientation), national origin, age, physical or mental
              disability, marital status, political affiliation, or retaliation, except for positions requiring bona
              fide occupational qualifications.
            </TextBlock>
          </Section>
        )}

        <Section title="Form A2 - Employment Application">
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>APPLICATION INSTRUCTIONS:</b> Give full and complete information. For questions which do not apply to
            you, please write "N/A" (Not Applicable). Your Social Security Number is necessary to maintain proper
            identification of your records. Refer to the page entitled "GENERAL INSTRUCTIONS & INFORMATION" for
            further information.
          </Typography>

          <FieldRow>
            <Field label="Position Title Applied For" value={data.positionTitle} />
            <Field label="Job Position No." value={data.jobPositionNo} />
            <Field label="Lowest salary acceptable" value={data.lowestSalaryAcceptable} />
          </FieldRow>

          <FieldRow columns="repeat(3, 1fr)">
            <Field label="Last Name" value={data.personal.lastName} />
            <Field label="First Name" value={data.personal.firstName} />
            <Field label="Middle Name" value={data.personal.middleName} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Mailing Address</Typography>
          <FieldRow columns="repeat(4, 1fr)">
            <Field label="P.O. Box or Street Number" value={data.personal.mailing.line} />
            <Field label="City" value={data.personal.mailing.city} />
            <Field label="State" value={data.personal.mailing.state} />
            <Field label="Zip Code" value={data.personal.mailing.zip} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Physical Address</Typography>
          <FieldRow columns="repeat(4, 1fr)">
            <Field label="Street or Lot Number" value={data.personal.physical.line} />
            <Field label="City" value={data.personal.physical.city} />
            <Field label="State" value={data.personal.physical.state} />
            <Field label="Zip Code" value={data.personal.physical.zip} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Phone No.</Typography>
          <FieldRow columns="repeat(3, 1fr)">
            <Field label="Home" value={data.personal.phone.home} />
            <Field label="Email" value={data.personal.phone.email} />
            <Field label="Cellphone Number" value={data.personal.phone.cell} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
            EDUCATION: Please check and indicate all of your formal educational accomplishments.
          </Typography>
          <CheckItem label="High School Graduate School:" checked={!!data.education.highSchool.school} />
          <FieldRow columns="repeat(3, 1fr)">
            <Field label="School" value={data.education.highSchool.school} />
            <Field label="Location" value={data.education.highSchool.location} />
            <Field label="Year Graduated" value={data.education.highSchool.yearGraduated} />
          </FieldRow>

          <CheckItem label="Completed G.E.D. School:" checked={!!data.education.ged.school} />
          <FieldRow columns="repeat(4, 1fr)">
            <Field label="School" value={data.education.ged.school} />
            <Field label="Location" value={data.education.ged.location} />
            <Field label="Certificate No." value={data.education.ged.certificateNo} />
            <Field label="Year Graduated" value={data.education.ged.yearGraduated} />
          </FieldRow>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <CheckItem label="Indicate Last Grade Completed in High School (select one):" checked={!!data.education.lastGradeCompleted} />
            {["9th", "10th", "11th"].map((g) => (
              <CheckItem key={g} label={g} checked={data.education.lastGradeCompleted === g} />
            ))}
          </Box>
          <Field label="School" value={data.education.lastGradeSchool} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>HIGHER EDUCATION</Typography>
          <Typography variant="caption" sx={{ float: "right" }}>Credit Hours Completed</Typography>
          <FieldRow columns="repeat(4, 1fr)">
            <Field label="Name of College/University" value={data.education.higherEducation.school} />
            <Field label="Location" value={data.education.higherEducation.location} />
            <Field label="Semester" value={data.education.higherEducation.semester} />
            <Field label="Quarter" value={data.education.higherEducation.quarter} />
          </FieldRow>
          <FieldRow columns="repeat(4, 1fr)">
            <Field label="From Date" value={data.education.higherEducation.fromDate || "mm/dd/yyyy"} />
            <Field label="End Date" value={data.education.higherEducation.endDate || "mm/dd/yyyy"} />
            <Field label="Course of Study" value={data.education.higherEducation.courseOfStudy} />
            <Field label="Type of Degree" value={data.education.higherEducation.degreeType} />
          </FieldRow>
          <Field label="Year Earned" value={data.education.higherEducation.yearEarned} sx={{ maxWidth: 200 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Undergraduate Courses</Typography>
          <FieldRow columns="repeat(3, 1fr)">
            <Field label="Major Undergraduate Courses" value={data.education.undergraduateCourses.major} />
            <Field label="Semester Hours" value={data.education.undergraduateCourses.semesterHours} />
            <Field label="Quarter Hours" value={data.education.undergraduateCourses.quarterHours} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Graduate Courses</Typography>
          <FieldRow columns="repeat(3, 1fr)">
            <Field label="Major Graduate Courses" value={data.education.graduateCourses.major} />
            <Field label="Semester Hours" value={data.education.graduateCourses.semesterHours} />
            <Field label="Quarter Hours" value={data.education.graduateCourses.quarterHours} />
          </FieldRow>

          <Field
            label="List Manuals, Equipment, Licenses, Special Training, and/or Certificates Pertinent to the Position Applied For"
            value={data.education.manualsEquipmentLicenses}
            sx={{ mt: 1 }}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>Work Experience</Typography>
          <TextBlock>
            This portion must be accurate and complete. Please be clear and concise. Under A, please indicate whether
            it is your PRESENT OR LAST EMPLOYER IF NOT CURRENTLY EMPLOYED. You may list your entire work history,
            including part-time, volunteer and temporary assignments. List jobs in order starting with your present
            job, or last job if you are unemployed. List each promotion as a separate job. Duties should include most
            difficult or most important responsibilities, and/or most significant accomplishments in the position
            held, to include percentage of time spent. Supervisory experience is a combination of subject matter
            knowledge and skills and/or managerial abilities related to getting the work done through other people.
          </TextBlock>

          {data.workExperiences.map((exp, index) => (
            <Box key={index} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
              <FieldRow columns="1.5fr 1fr 1fr">
                <Field label="Name of Employer" value={exp.employer} />
                <Field label="Start Date" value={exp.startDate || "mm/dd/yyyy"} />
                <Field label="End Date" value={exp.endDate || "mm/dd/yyyy"} />
              </FieldRow>
              <Box sx={{ mb: 1 }}>
                <CheckItem label="Present Employer" checked={exp.isPresentEmployer} />
                <CheckItem label="Last Employer" checked={exp.isLastEmployer} />
              </Box>

              <FieldRow columns="repeat(3, 1fr)">
                <Field label="Hours Worked Per Week" value={exp.hoursPerWeek} />
                <Field label="Position Title" value={exp.positionTitle} />
                <Field label="Salary" value={exp.salary} />
              </FieldRow>
              <FieldRow columns="repeat(2, 1fr)">
                <Field label="Reason for Leaving" value={exp.reasonForLeaving} />
                <Field label="Type of Business (i.e. Construction)" value={exp.businessType} />
              </FieldRow>

              <Typography variant="body2" sx={{ fontWeight: 600 }}>This position is:</Typography>
              <Box sx={{ mb: 1 }}>
                <CheckItem label="Supervisory" checked={exp.supervisory} />
                <CheckItem label="Permanent" checked={exp.permanent} />
                <br />
                <CheckItem label="Non-Supervisory" checked={exp.nonSupervisory} />
                <CheckItem label="Temporary" checked={exp.temporary} />
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 600 }}>Employer's Mailing Address</Typography>
              <FieldRow columns="repeat(4, 1fr)">
                <Field label="P.O. Box or Street Number" value={exp.employerAddress.line} />
                <Field label="City" value={exp.employerAddress.city} />
                <Field label="State" value={exp.employerAddress.state} />
                <Field label="Zip Code" value={exp.employerAddress.zip} />
              </FieldRow>
              <FieldRow columns="repeat(2, 1fr)">
                <Field label="Employer's Telephone No." value={exp.employerPhone} />
                <Field label="Immediate Supervisor" value={exp.immediateSupervisor} />
              </FieldRow>

              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>SPECIFIC DUTIES PERFORMED:</Typography>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                {exp.specificDuties.map((duty, i) => (
                  <Box key={i} sx={{ px: 1.5, py: 1, borderBottom: i < exp.specificDuties.length - 1 ? "1px solid" : "none", borderColor: "divider", minHeight: 20 }}>
                    {duty || " "}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

          <Field
            label="Are you currently an employee of a Government of Guam autonomous agency?"
            required
            sx={{ maxWidth: 300 }}
            value={data.isGovGuamAutonomousEmployee ? "Yes" : "No"}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>PREFERENTIAL HIRE STATUS</Typography>
          <TextBlock>
            This applies only to first time applicants of government of Guam Merit Scholarship or Educational Loan
            Recipients. If you wish to claim Preferential Hire Status, please check "Yes" and attach letter of
            eligibility, if not, check "N/A. This status is applicable only for initial employment with the
            government of Guam. Approval of claim is subject to verification.
          </TextBlock>
          <Field
            label='If applicable, please specify previous applications in which you claimed preferential hire status. If yes, please specify:'
            required
            value={data.preferentialHireStatus}
            sx={{ maxWidth: 300 }}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>PERSONAL CONTACT</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            (Optional: In the event that we are unable to contact you, please give three names for reference.)
          </Typography>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", mb: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", backgroundColor: "#f5f7fa", fontWeight: 600, fontSize: 13 }}>
              <Box sx={{ p: 1 }}>Name:</Box>
              <Box sx={{ p: 1 }}>Address:</Box>
              <Box sx={{ p: 1 }}>Telephone No.</Box>
            </Box>
            {data.personalContacts.map((contact, i) => (
              <Box key={i} sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", borderTop: "1px solid", borderColor: "divider" }}>
                <Box sx={{ p: 1 }}>{contact.name || " "}</Box>
                <Box sx={{ p: 1 }}>{contact.address || " "}</Box>
                <Box sx={{ p: 1 }}>{contact.phone || " "}</Box>
              </Box>
            ))}
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            I hereby certify that, to the best of my knowledge, the provided information is true and accurate.
          </Typography>
          <SignatureBox label="Signature of Applicant" signatureUrl={data.applicantSignatureA2} />
        </Section>

        <Section title="Form A3 - Preference Points Request Form">
          <TextBlock>
            This form is used to award preference points for Veterans of the Armed Forces of the United States or the
            Guam Police Combat Patrol and Persons with a disability. This form is separate and apart from the job
            application. IF APPLYING FOR MORE THAN ONE POSITION, YOU MUST COMPLETE THIS FORM FOR EACH APPLICATION
            SUBMITTED IN ORDER TO RECEIVE CREDIT FOR EACH POSITION APPLIED.
          </TextBlock>

          <FieldRow>
            <Field label="Position Title Applied For" value={data.positionTitle} />
            <Field label="Job Position No." value={data.jobPositionNo} />
          </FieldRow>
          <FieldRow columns="repeat(3, 1fr)">
            <Field label="Last Name" value={data.personal.lastName} />
            <Field label="First Name" value={data.personal.firstName} />
            <Field label="Middle Name" value={data.personal.middleName} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>1. Preference points for veterans or police combat patrol</Typography>
          <Typography variant="body2">Please indicate:</Typography>
          <Box sx={{ mb: 1 }}>
            <CheckItem label="N/A" checked={data.preferencePoints.veteranOption === "NA"} />
            <br />
            <CheckItem label="5 preference points: (Provide DD-214 Member 4, which indicates service dates)." checked={data.preferencePoints.veteranOption === "5"} />
            <Typography variant="caption" sx={{ ml: 1 }}>* Upload must be in PDF format only</Typography>
            <br />
            <CheckItem label="10 preference points: (Disabled Veteran) (Please provide U.S. Department of Veterans Affairs letter)." checked={data.preferencePoints.veteranOption === "10"} />
            <Typography variant="caption" sx={{ display: "block", ml: 1 }}>* Upload must be in PDF format only</Typography>
          </Box>

          <FieldRow columns="repeat(4, 1fr)">
            <Field label="Branch" value={data.preferencePoints.branch} />
            <Field label="Type of Discharge" value={data.preferencePoints.dischargeType} />
            <Field label="Start Date" value={data.preferencePoints.startDate || "mm/dd/yyyy"} />
            <Field label="End Date" value={data.preferencePoints.endDate || "mm/dd/yyyy"} />
          </FieldRow>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>2. Preference points for persons with disabilities</Typography>
          <Typography variant="body2">Please indicate:</Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box>
              <CheckItem label="N/A" checked={data.preferencePoints.disabilityOption === "NA"} />
              <br />
              <CheckItem label="5 preference points: (Attach certification from Department of Public Health)." checked={data.preferencePoints.disabilityOption === "5"} />
              <Typography variant="caption" sx={{ ml: 1 }}>* Upload must be in PDF format only</Typography>
            </Box>
            <Field label="Date of Certification" value={data.preferencePoints.dateOfCertification || "mm/dd/yyyy"} sx={{ minWidth: 180 }} />
          </Box>

          <TextBlock>
            APPROVAL OF POINTS IS SUBJECT TO VERIFICATION. PLEASE SUBMIT YOUR APPROPRIATE DOCUMENTS SUCH AS DD214
            MEMBER 4, V.A. SERVICE CONNECTED DISABILITY DOCUMENT OR CERTIFICATION FROM PUBLIC HEALTH. FOR VETERANS,
            YOUR DOCUMENT MUST SHOW THAT YOU SERVED A MINIMUM OF 180 CUMULATIVE DAYS OF ACTIVE DUTY AND RECEIVED OTHER
            THAN A DISHONORABLE DISCHARGE. PLEASE SEE GENERAL INSTRUCTION PAGE FOR MORE INFORMATION. PLEASE NOTE,
            THESE PREFERENCE POINTS ARE ADDED TO AN APPLICANT'S PASSING SCORE, IT CANNOT BE USED TO QUALIFY AN
            OTHERWISE UNQUALIFIED APPLICANT.
          </TextBlock>

          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            I hereby certify that, to the best of my knowledge, the provided information is true and accurate.
          </Typography>
          <SignatureBox label="Signature of Applicant" signatureUrl={data.applicantSignatureA3} />
        </Section>

        {data.applicationInfo && (
          <Section title="Application Information">
            <Field label="EXPERIENCE (WITHIN LAST 5 YEARS)" value={data.applicationInfo.experience} bulletList />
            <Field label="REQUIRED SKILLS (WITHIN LAST 5 YEARS)" value={data.applicationInfo.requiredSkills} bulletList />
            <Field label="ADDITIONAL SKILLS (NICE-TO-HAVE OR PREFERRED SKILL)" value={data.applicationInfo.additionalSkills} bulletList />
            <Field label="CERTIFICATIONS AND LICENSES (MUST BE ACTIVE)" value={data.applicationInfo.certifications} bulletList />
            <Field label="EMPLOYMENT (IF AN ACTIVE GOVT OR GOVT DEPARTMENT)" value={data.applicationInfo.employment} bulletList />
            <Field label="Description (IF ANY OTHER INFORMATION YOU WOULD LIKE TO SHARE TO PREPARE YOUR RESUME)" value={data.applicationInfo.description} bulletList />
          </Section>
        )}

        <Section title="Document Attachment">
          <TextBlock>
            Listed below are the documents needed for applying for this job announcement. Please be advised that
            mandatory document must be submitted, failure to submit these documents will render your application
            incomplete and will not be considered for this job announcement.
          </TextBlock>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Minimum Educational Requirements:</Typography>
          <TextBlock>
            All new employment in the service of the government of Guam shall have, as reasonable measure of job
            performance, minimum requirement of a high school diploma or a successful completion of General Education
            Development (GED) test or any equivalent of a general education high school program, apprenticeship
            program or successful completion of a certification program, from a recognized, accredited or certified
            vocational technical institution, in a specialized field required for the job, pursuant to Public Law
            29-113.
          </TextBlock>

          {data.documentAttachments.map((doc, index) => (
            <DocumentCard key={index} label={doc.label} fileName={doc.fileName} documentUrl={doc.documentUrl} />
          ))}
        </Section>

        {data.appeal && (
          <>
            <Box ref={appealSpacerRef} sx={{ height: 0 }} />
            <Box ref={appealSectionRef} sx={{ pageBreakBefore: "always", breakBefore: "page" }}>
              <Section title="Appeal Information">
                <Field label="Appeal Date" value={data.appeal.date} sx={{ maxWidth: 200 }} />
                <Field label="Remarks" value={data.appeal.remarks} />

                {data.appeal.documents.map((doc, index) => (
                  <DocumentCard key={index} label={doc.label} fileName={doc.fileName} documentUrl={doc.documentUrl} />
                ))}
              </Section>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
});

export default ApplicationPdfPage;
